import React, { Fragment, Component } from 'react';
import ReactDOMServer, { renderToString } from 'react-dom/server';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import axios from 'axios';
import Cookie from 'react-cookies';
import moment from 'moment-timezone';
import I18n from 'i18n';
import { decode } from 'html-entities';
import * as Routes from 'routes';
import ActionsDropdown from 'common/ActionsDropdown';
import AdvancedFilters from 'common/AdvancedFilters';
import { buildFilterPath, buildQueryPath, filtersPropTypes } from 'common/AdvancedFilters/Utils';
import PaginatedTable from 'common/DataTable/PaginatedTable';
import AssignmentModal from 'staffer/AssignmentModal';
import { assessmentsPropType, classroomsPropType } from 'staffer/AssignmentModal/PropTypes';
import useAssignablesStore from 'staffer/AssignmentModal/WhatSection/store/useAssignablesStore';
import SettingsShortcut from 'common/ShortcutButtons/SettingsShortcut';
import { TCITooltip } from '@/components/ui/Tooltip';
import { sanitizeInlineHtml } from '@/utils/sanitizeInlineHtml';
import assignmentLmsKind from '@/components/shared/Lms/assignmentLmsKind';
import { LTI_KINDS } from '@/components/shared/Lms/lmsProviders';
import { redirectLinks } from '../../../modules/ExternalRedirect';
import Add from './Add';
import Delete from './Delete';
import Edit from './Edit';
import SyncGrades from './SyncGrades';
import ViewSyncActivity from './ViewSyncActivity';
import OpenInGradeSync from './OpenInGradeSync';
import styles from './AssignmentsTable.module.scss';
import { formatAssignments, formattedAssignment } from './Utils';
import AssignmentDetails, {
  ASSIGNMENT_DETAILS_TOOLTIP_CLASS,
  AssignmentNotStartedInfoIcon,
  PAST_DUE_LOCK_TOOLTIP_CLASS,
  PastEnforcedDueDateLockIcon,
} from './AssignmentDetails';
import AssignmentFilterButton from './AssignmentFilterButton';
import LmsIcon from './LmsIcon';
import CoTeacherBadge from '../CoTeacherBadge';

// Gives RefreshCourseWorkStateWorker time to write the state the first refetch enqueued it to check.
const GOOGLE_DRAFT_RECHECK_DELAY_MS = 10000;
// Focus fires on every tab or app switch; this caps the refetches one open page can send.
const GOOGLE_DRAFT_MIN_INTERVAL_MS = 30000;

export default class AssignmentsTable extends Component {
  static propTypes = {
    assessments: assessmentsPropType,
    assessmentsPagePath: PropTypes.string,
    assignmentAssignmentsModelsPath: PropTypes.string, // staffer
    assignmentCounts: PropTypes.shape({
      all: PropTypes.number,
      completed: PropTypes.number,
      pastDue: PropTypes.number,
      toDo: PropTypes.number
    }),
    assignmentsHash: PropTypes.any,
    assignmentsPath: PropTypes.string,
    classroomId: PropTypes.number,
    classrooms: classroomsPropType,
    coTeacherAssignmentVisibility: PropTypes.bool,
    columns: PropTypes.arrayOf(PropTypes.string).isRequired,
    connectedToCanvas: PropTypes.bool,
    connectedToGoogleClassroom: PropTypes.bool,
    defaultExpandRows: PropTypes.bool,
    destroyPath: PropTypes.string,
    hideButtons: PropTypes.bool,
    inDashboard: PropTypes.bool,
    info: PropTypes.bool,
    initialFilters: filtersPropTypes,
    lastViewed: PropTypes.string,
    lessonAutofillOptions: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
    })),
    ltiKind: PropTypes.oneOf(LTI_KINDS),
    pageLength: PropTypes.number,
    pageSize: PropTypes.number,
    paging: PropTypes.bool,
    program: PropTypes.shape({
      display_investigations_text_section: PropTypes.bool.isRequired,
      display_unit_content: PropTypes.bool.isRequired,
      hasVideoActivity: PropTypes.bool.isRequired,
      id: PropTypes.number.isRequired,
      science: PropTypes.bool.isRequired
    }).isRequired,
    retroactiveGradeSync: PropTypes.bool,
    setAssignments: PropTypes.func,
    showPagingOptions: PropTypes.bool,
    studentId: PropTypes.number,
    subscriberConnectedToCanvas: PropTypes.bool,
    teacherAutofillOptions: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
    })),
    updatePath: PropTypes.string,
    userId: PropTypes.number.isRequired,
    userType: PropTypes.string,
  };

  static defaultProps = {
    assessments: [],
    assessmentsPagePath: '',
    assignmentAssignmentsModelsPath: '',
    assignmentCounts: {
      all: null,
      completed: null,
      pastDue: null,
      toDo: null
    },
    assignmentsPath: '',
    classroomId: null,
    classrooms: [],
    coTeacherAssignmentVisibility: false,
    connectedToCanvas: false,
    connectedToGoogleClassroom: false,
    defaultExpandRows: true,
    destroyPath: '',
    hideButtons: false,
    inDashboard: false,
    info: true,
    initialFilters: null,
    lastViewed: null,
    lessonAutofillOptions: null,
    ltiKind: null,
    pageLength: 20,
    pageSize: 20,
    paging: true,
    retroactiveGradeSync: false,
    setAssignments: () => {},
    showPagingOptions: true,
    studentId: null,
    subscriberConnectedToCanvas: false,
    teacherAutofillOptions: [],
    updatePath: '',
    userType: null,
  };

  static formatAssignmentsModelsDropdown(assignmentsModels, className) {
    if (!assignmentsModels) return null;
    return assignmentsModels.map(model => (
      <li key={`${model.label} ${model.href}`} className={`${styles['dropdown-item']} ${className} ${styles.listItem}`}>
        <a
          href={model.href}
          rel="noopener noreferrer"
          target="_blank"
        >
          {model.label}
        </a>
      </li>
    ));
  }

  static showLock(datesEnforced) {
    if (datesEnforced) {
      return (
        <span className="ml10">
          <TCITooltip>
            <TCITooltip.Trigger asChild>
              <i aria-label="This assignment will be locked after the due date." className="fa fa-lock" role="img" />
            </TCITooltip.Trigger>
            <TCITooltip.Content>
              This assignment will be locked after the due date.
            </TCITooltip.Content>
          </TCITooltip>
        </span>
      );
    }

    return null;
  }

  static classroomDiv(classroom) {
    return (
      <div key={classroom.id}>
        Period {classroom.period} - {classroom.name}
      </div>
    );
  }

  constructor(props) {
    super(props);
    const isCollapsed = Cookie.load('isCollapsed');
    // TODO: had to remove the lesson assoc ({ assignments_models: { model: 'lesson' } })
    // because of the state capitals game. leaving a comment for visibility in case
    // performance seems to be a concern
    const includes = ['assignments_models', 'lms_provider', 'program',
      { assignee_classrooms: 'staffer_classrooms', classrooms: 'staffer_classrooms' }];
    const stafferIncludes = [...includes, 'assignment_events', 'google_course_works'];

    if (props.studentId) {
      this.getPathBase = Routes.plato_api_student_index_student_assignments_path(
        props.userId,
        {
          for_classroom: props.classroomId,
          in_program: props.program.id,
          includes,
          only: [
            'assignment_label', 'assignment_location', 'assignment_models_hash', 'classes', 'classroom_assignment',
            'connected_to_canvas', 'connected_to_google', 'connected_to_schoology', 'created_at', 'dates_enforced',
            'due_datetime', 'first_lesson_id', 'first_unit_id', 'grading_notebook_by', 'has_sync_errors', 'id',
            'lms_provider', 'lms_type', 'max_score', 'name', 'notes', 'num_students', 'randomize_answers',
            'show_answers_to_students', 'staffer_id', 'start_datetime', 'status', 'teacher_display_name',
            'teacher_name', 'updated_at',
          ],
          page: 'pageNumber',
          per_page: 20
        }
      );
    }
    else {
      this.getPathBase = Routes.plato_api_staffer_assignments_path(
        props.userId,
        {
          extra_attributes: ['can_destroy', 'can_edit_max_score', 'has_sync_errors?', 'pending_publish_on_google?'],
          in_program: props.program.id,
          includes: stafferIncludes,
          local_time_zone: moment.tz.guess(),
          only: [
            'assignment_label', 'assignment_location', 'assignment_models_hash', 'classes', 'classroom_assignment',
            'connected_to_canvas', 'connected_to_google', 'connected_to_schoology', 'created_at', 'dates_enforced',
            'due_datetime', 'first_lesson_id', 'first_unit_id', 'grading_notebook_by', 'has_sync_errors', 'id',
            'lms_provider', 'lms_type', 'max_score', 'name', 'not_finalized_on_lti', 'notes', 'num_students',
            'pending_publish_on_google', 'randomize_answers',
            'show_answers_to_students', 'start_datetime', 'status', 'teacher_name', 'updated_at', 'staffer_id',
            'teacher_display_name'
          ],
          page: 'pageNumber',
          per_page: 20
        }
      );
    }

    const params = new URLSearchParams(window.location.search);
    const activeToggle = params.get('activeToggle');
    if (this.props.studentId) {
      this.state = {
        activeFilters: {},
        activeToggle: activeToggle || 'to_do',
        doneLoading: false,
        loadingInitialValues: false,
        filterSent: false,
        getPath: this._initialGetPath(),
        initialIsUnit: false,
        initialLessonId: null,
        initialValues: {},
        isCollapsed: false,
        modalIsOpen: false,
        newRecord: true,
        pagesLoaded: 0,
        resources: []
      };
    }
    else {
      this.state = {
        activeFilters: {},
        activeToggle: activeToggle || 'all',
        doneLoading: false,
        loadingInitialValues: false,
        filterSent: false,
        getPath: this._initialGetPath(),
        initialIsUnit: false,
        initialLessonId: null,
        initialValues: {},
        isCollapsed: !isCollapsed || isCollapsed === 'true',
        modalIsOpen: false,
        newRecord: true,
        pagesLoaded: 0,
        resources: []
      };
    }

    this.closeAssignmentModal = this.closeAssignmentModal.bind(this);
    this.openAssignmentModal = this.openAssignmentModal.bind(this);
    this.getAssignmentsModels = this.getAssignmentsModels.bind(this);
    this.renderActionsDropdown = this.renderActionsDropdown.bind(this);
    this.updateResources = this.updateResources.bind(this);
    this._updateActiveFilters = this._updateActiveFilters.bind(this);
    this._addResources = this._addResources.bind(this);
    this._resetResources = this._resetResources.bind(this);
    this.renderDatesEnforcedCell = this.renderDatesEnforcedCell.bind(this);
    this.renderClassColumn = this.renderClassColumn.bind(this);
    this.formatLabel = this.formatLabel.bind(this);
    this.formatClasses = this.formatClasses.bind(this);
    this.renderClassesCell = this.renderClassesCell.bind(this);
    this.tooltipContent = this.tooltipContent.bind(this);
    this.toggleCollapse = this.toggleCollapse.bind(this);
    this.handleDataTableInit = this.handleDataTableInit.bind(this);
    this.adjustColumnsOnResize = this.adjustColumnsOnResize.bind(this);
    this.refreshGoogleDrafts = this.refreshGoogleDrafts.bind(this);
    this.fetchGoogleDrafts = this.fetchGoogleDrafts.bind(this);
  }

  componentDidMount() {
    if (!this.props.studentId) window.addEventListener('focus', this.refreshGoogleDrafts);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.adjustColumnsOnResize);
    window.removeEventListener('focus', this.refreshGoogleDrafts);
    if (this._resizeTimer) clearTimeout(this._resizeTimer);
    if (this._googleDraftTimer) clearTimeout(this._googleDraftTimer);
  }

  // AIDEV-NOTE: Google never tells us a draft was published. Teachers publish in another tab, so re-check pending
  // rows when they come back: the first fetch enqueues the worker, the delayed one picks up what it wrote.
  refreshGoogleDrafts() {
    if (!this.state.resources.some(assignment => assignment.pending_publish_on_google)) return;
    if (Date.now() - (this._googleDraftRefreshedAt || 0) < GOOGLE_DRAFT_MIN_INTERVAL_MS) return;

    this._googleDraftRefreshedAt = Date.now();
    if (this._googleDraftTimer) clearTimeout(this._googleDraftTimer);
    this.fetchGoogleDrafts();
    this._googleDraftTimer = setTimeout(this.fetchGoogleDrafts, GOOGLE_DRAFT_RECHECK_DELAY_MS);
  }

  fetchGoogleDrafts() {
    const ids = this.state.resources
      .filter(assignment => assignment.pending_publish_on_google)
      .map(assignment => assignment.id);
    if (ids.length === 0) return Promise.resolve();

    // getPathBase carries PaginatedTable's `page=pageNumber` placeholder; drop paging to get every pending row.
    const url = new URL(this.getPathBase, window.location.origin);
    url.searchParams.delete('page');
    url.searchParams.delete('per_page');

    return axios.get(`${url.pathname}${url.search}`, { params: { 'search[id_in]': ids } }).then(({ data }) => {
      const refreshed = new Map(data.data.map(assignment => [assignment.id, formattedAssignment(assignment)]));

      this.setState(prevState => ({
        resources: prevState.resources.map(assignment => refreshed.get(assignment.id) || assignment),
      }));
    });
  }

  // PLAT-17220: DataTables computes column + table widths in pixels once at init
  // (autoWidth: true) and writes them as inline styles. There's no built-in resize
  // observer, so a viewport that grows after a narrow load keeps the narrow widths.
  // Stash the API on init and re-run `columns.adjust()` on window resize.
  handleDataTableInit(settings) {
    // AIDEV-NOTE: settings.oInstance is an internal DataTables property, used here intentionally.
    // The documented `$(settings.nTable).DataTable()` path re-resolves the API via jQuery, but
    // jQuery isn't imported in this module; oInstance is the API object DataTables already built.
    // Version is pinned, so this is stable — leave as-is (auto-review: ignore).
    this._dataTableApi = settings.oInstance.api();
    window.addEventListener('resize', this.adjustColumnsOnResize);
  }

  adjustColumnsOnResize() {
    if (this._resizeTimer) clearTimeout(this._resizeTimer);
    this._resizeTimer = setTimeout(() => {
      if (this._dataTableApi) this._dataTableApi.columns.adjust();
    }, 150);
  }

  _initialGetPath() {
    if (this.props.studentId) return buildQueryPath(this.getPathBase, {});

    const initialFilterHash = {};

    // setting up initial filters hash here because leaving it to advanced filters failed
    if (this.getInitialFilters()) {
      this.getInitialFilters().map((filter) => {
        if (filter.value) {
          initialFilterHash[`${filter.propertyValue}_${filter.operatorValue}`] = filter.value;
        }

        return null;
      });
    }

    return buildFilterPath(this.getPathBase, initialFilterHash);
  }

  tooltipContent(classes) {
    const values = this.props.classrooms.filter(classroom => classes.map(c => c.id).includes(classroom.id));

    return (
      <div>
        {values.map(classroom => AssignmentsTable.classroomDiv(classroom))}
      </div>
    );
  }

  formatClasses(classes) {
    if (classes.length === 0) return <span>None</span>;
    if (classes.length === 1) return this.formatSingleClass(classes[0]);

    const classesLength = this.props.classrooms.length === classes.length ? 'All' : classes.length;
    const classNames = classes.map(c => c.name).join(', ');

    return (
      <TCITooltip>
        <TCITooltip.Trigger asChild>
          <span tabIndex={0}>
            <span aria-hidden="true">{classesLength}</span>
            <span className="tw-sr-only">{`${classesLength} classes: ${classNames}`}</span>
          </span>
        </TCITooltip.Trigger>
        <TCITooltip.Content>
          {this.tooltipContent(classes)}
        </TCITooltip.Content>
      </TCITooltip>
    );
  }

  formatSingleClass(classData) {
    const classroom = this.props.classrooms.find(c => c.id === classData.id);
    const tooltipText = classroom ?
      `Period ${classroom.period} - ${classroom.name}` :
      classData.name;

    return (
      <TCITooltip>
        <TCITooltip.Trigger asChild>
          <span className="tw-block tw-max-w-[7.5rem] tw-truncate" tabIndex={0}>
            {classroom && <span className="tw-sr-only">{`Period ${classroom.period} - `}</span>}
            {classData.name}
          </span>
        </TCITooltip.Trigger>
        <TCITooltip.Content>
          {tooltipText}
        </TCITooltip.Content>
      </TCITooltip>
    );
  }

  renderClassesCell(td, cellData, rowData) {
    ReactDOM.render(
      this.formatClasses(rowData.classes),
      td
    );
  }

  renderClassColumn(data, type, row) {
    if (row.classes.length === 0) return renderToString(<span>None</span>);

    const classroomNames = row.classes.map(classroom => classroom.name).sort();
    return renderToString(<span>{decode(classroomNames.join(', '))}</span>);
  }

  formatLabel(data, type, row) {
    ReactDOM.render(
      <div data-assignment-id={row.id}>
        <div>
          {this.props.studentId && this.renderViewedStatus(row)}
        </div>
        <div>
          <p className={styles.location}>
            {row.assignment_location}
          </p>
          <div
            className={styles.assignmentTitle}
            dangerouslySetInnerHTML={{ __html: sanitizeInlineHtml(row.assignment_label) }}
          />
        </div>

        <div className="tw-flex tw-gap-2">
          {!this.props.studentId && <LmsIcon assignment={row} />}
          {this.props.coTeacherAssignmentVisibility && this.props.userId !== row.staffer_id && (
            <div className="tw-mt-2">
              <CoTeacherBadge stafferId={row.staffer_id} teacherDisplayName={row.teacher_display_name} />
            </div>
          )}
        </div>
      </div>,
      data
    );
  }

  lmsKind(row) {
    const kind = assignmentLmsKind(row);
    const canvasApiDisconnected = row.lms_type === 'canvas' &&
      !(this.props.connectedToCanvas && this.props.subscriberConnectedToCanvas);

    return kind === 'canvas' && canvasApiDisconnected ? null : kind;
  }

  /**
   * Get assignments_models for a given assignment id.
   * @param assignmentId
   * @returns {Promise<AxiosResponse<any> | void>}
   */
  getAssignmentsModels(assignmentId, unitLessonId = undefined) {
    const params = {
      assignment_id: assignmentId,
      parent_id: unitLessonId,
      program_id: this.props.program.id
    };

    return axios.get(Routes.plato_api_get_initial_assignables_assignables_path(params))
      .then(response => response.data)
      .catch(e => console.log(e));
  }

  static getStudentIdsInClassroom(classroomIds) {
    if (classroomIds.length > 1) return [];

    return axios
      .get(Routes.plato_api_classroom_students_path(classroomIds[0]))
      .then((response) => {
        const students = response.data.data;

        return students.map(student => student.id);
      });
  }

  static getClassroomAssigneeModels(assignmentId, classroomAssignment) {
    return axios
      .get(Routes.plato_api_assignment_assignments_assignee_models_path(assignmentId))
      .then((response) => {
        const assignmentsAssigneeModels = response.data.data;

        if (classroomAssignment) {
          const classroomIds = assignmentsAssigneeModels.map(assigneeModel => assigneeModel.model_id);

          return {
            assignee_model_ids: classroomIds,
            classroomAssignment: classroomAssignment,
            classroom_student_ids: [], // ids of students in the assigned classroom
            student_classroom_ids: [] // ids of classrooms that the assignment was assigned to (only populated for individual student assignments)
          };
        }

        return {
          assignee_model_ids: [],
          classroom_student_ids: [],
          student_classroom_ids: [...new Set(assignmentsAssigneeModels.map(assigneeModel => assigneeModel.classroom_id))]
        };
      })
      .then((values) => {
        // if the assignment has only been assigned to one classroom, then we aso get the ids of all the students in the classroom
        if (values.assignee_model_ids.length === 1 && values.classroomAssignment) {
          return AssignmentsTable.getStudentIdsInClassroom(values.assignee_model_ids)
            .then(studentIds => Object.assign({}, values, { classroom_student_ids: studentIds }));
        }

        return values;
      })
      .catch(e => console.log(e));
  }

  static getStudentAssigneeModels(assignmentId) {
    return axios
      .get(Routes.plato_api_assignment_assignments_assignee_models_path(assignmentId),
        { params: { in_classroom_for_assignment: assignmentId } })
      .then((response) => {
        const assignmentsAssigneeModels = response.data.data;

        if (assignmentsAssigneeModels.length > 0) {
          // we return all the ids of the students the assignment has been assigned to
          // along with the id of the classroom the assignment has been assigned to
          return {
            assignee_model_ids: assignmentsAssigneeModels.map(assigneeModel => assigneeModel.model_id),
            student_classroom_ids: [assignmentsAssigneeModels[0].classroom_id]
          };
        }

        return {
          assignee_model_ids: [],
          student_classroom_ids: []
        };
      })
      .catch(e => console.log(e));
  }

  static getClassroomsAndAssignees(rowData) {
    // if an assignment has been assigned to all students, it's a classroom assignment
    if (rowData.students === 'All') {
      return AssignmentsTable.getClassroomAssigneeModels(rowData.id, rowData.classroom_assignment);
    }

    return AssignmentsTable.getStudentAssigneeModels(rowData.id);
  }

  /**
   * Create initial values object for an existing assignment.
   * @param rowData
   * @returns {null|{
   *   assignment_id:, due_datetime, assessment_ids:, lesson_assignable_ids:,
   *   student_classroom_ids:, assignee_model_ids:
   * }}
   */
  getInitialValues(rowData) {
    if (!rowData) return null;

    const unitLessonId = rowData.first_lesson_id || rowData.first_unit_id;

    this.setState({
      initialIsUnit: !rowData.first_lesson_id && !!rowData.first_unit_id,
      initialLessonId: unitLessonId,
      loadingInitialValues: true
    });

    // values for What/When section (assessment_ids, lesson_assignable_ids)
    return this
      .getAssignmentsModels(rowData.id, unitLessonId)
      .then((assignables) => {
        const initialValues = {
          assignment_id: rowData.id,
          can_edit_max_score: rowData.can_edit_max_score,
          dates_enforced: rowData.dates_enforced,
          due_datetime: rowData.due_datetime,
          grading_notebook_by: rowData.grading_notebook_by,
          max_score: rowData.max_score,
          name: rowData.name,
          notes: rowData.notes,
          randomize_answers: rowData.randomize_answers,
          show_answers_to_students: rowData.show_answers_to_students,
          start_datetime: rowData.start_datetime,
        };

        return Object.assign(initialValues, assignables);
      })
      .then((values) => {
        // values for Who section (student_classroom_ids, assignee_model_ids)
        AssignmentsTable.getClassroomsAndAssignees(rowData)
          .then((classroomsAndAssignees) => {
            const initialValues = Object.assign({}, values, classroomsAndAssignees);
            this.setState({ initialValues, loadingInitialValues: false });
            return initialValues;
          })
          .catch(() => this.setState({ loadingInitialValues: false }));
      })
      .catch(() => this.setState({ loadingInitialValues: false }));
  }

  /**
   * Returns the filters passed in if there are any, otherwise returns the default filters.
   * This done here so that we can pass in a local timestamp for the user.
   * @returns {array} - an array of objects representing the initial filters
   */
  getInitialFilters() {
    if (this.props.studentId) return null;

    if (this.props.initialFilters) {
      return this.props.initialFilters;
    }

    const filters = [
      { operatorValue: 'gteq', propertyValue: 'due_datetime' },
      { operatorValue: 'eq', propertyValue: 'assignments_models_lesson_id' }
    ];

    if (this._canFilterByTeacher()) {
      filters.push({ operatorValue: 'eq', propertyValue: 'staffer_id' });
    }

    return filters;
  }

  openAssignmentModal(newRecord, rowData = null) {
    this.getInitialValues(rowData);
    this.setState({ modalIsOpen: true, newRecord });
  }

  closeAssignmentModal() {
    this.setState({ initialIsUnit: false, initialLessonId: null, initialValues: {}, modalIsOpen: false });
    useAssignablesStore.getState().resetState();
  }

  /**
   * Used to update resources after creation of assignments
   * @param data Serialized assignments from response
   * @param method is the action being performed
   */
  updateResources(data, method) {
    if (method === 'create') {
      this.addResource(data);
    }
    else if (method === 'update') {
      this.editResource(data);
    }
    else if (method === 'delete') {
      this.deleteResource(data);
    }
  }

  addResource(data) {
    const assignment = formattedAssignment(data);

    const resources = [...this.state.resources];
    resources.push(assignment);
    this.setState({ resources });
  }

  editResource(data) {
    const resources = this.state.resources.filter(assignment => assignment.id !== data.id);
    const updatedAssignment = formattedAssignment(data);
    resources.push(updatedAssignment);
    this.setState({ resources });
  }

  deleteResource(data) {
    const resources = this.state.resources.filter(assignment => assignment.id !== data.id);
    const state = { resources };
    state.resources = resources;
    this.setState(state);
  }

  columnDefs() {
    const toggleIcon = '<button class="no_button_actions"><i class="fa toggle-icon purple" /></button>';

    if (this.props.studentId) {
      return (
        [
          {
            ariaTitle: 'Expand/Collapse Assignment Name',
            className: styles.expandCollapseWrapper,
            render: () => toggleIcon,
            sortable: false,
            targets: 0,
            title: '',
            width: '2%'
          },
          {
            createdCell: this.formatLabel,
            targets: 1,
            title: I18n.t('assignment_name'),
            width: '30%'
          },
          {
            targets: 2,
            title: I18n.t('start_date'),
            type: 'date',
            width: '15%'
          },
          {
            targets: 3,
            title: I18n.t('due_date'),
            type: 'date',
            width: '15%',
            createdCell: this.renderDatesEnforcedCell
          }
        ]
      );
    }

    return (
      [
        {
          className: styles.expandCollapseWrapper,
          targets: 0,
          title: '',
          ariaTitle: 'Expand/Collapse Assignment Name',
          sortable: false,
          render: () => toggleIcon
        },
        {
          targets: 2,
          title: I18n.t('class'),
          createdCell: this.renderClassesCell,
          width: '15%'
        },
        { // Students
          targets: 3,
          title: I18n.t('students'),
          width: '10%',
        },
        {
          createdCell: this.formatLabel,
          targets: 1,
          title: I18n.t('assignment_name'),
          width: '35%'
        },
        {
          targets: 4,
          title: I18n.t('start_date'),
          type: 'date'
        },
        {
          targets: -2,
          title: I18n.t('due_date'),
          type: 'date',
          createdCell: this.renderDatesEnforcedCell
        },
        { // Actions
          className: 'actions-column',
          title: I18n.t('actions'),
          targets: -1,
          sortable: false,
          width: '10%',
          searchable: false,
          createdCell: this.renderActionsDropdown
        }
      ]
    );
  }

  defaultOrder() {
    return this.props.studentId ? [3, 'asc'] : [5, 'desc'];
  }

  renderAdd() {
    if (this.props.studentId) return null;

    return <Add openAssignmentModal={this.openAssignmentModal} />;
  }

  renderAssignmentModal() {
    if (this.props.studentId) return null;

    return (
      <AssignmentModal
        assessments={this.props.assessments}
        assessmentsPagePath={this.props.assessmentsPagePath}
        assignmentsPath={this.props.assignmentsPath}
        classrooms={this.props.classrooms}
        closeModal={this.closeAssignmentModal}
        connectedToCanvas={this.props.connectedToCanvas}
        connectedToGoogleClassroom={this.props.connectedToGoogleClassroom}
        handlePostSave={this.updateResources}
        initialIsUnit={this.state.initialIsUnit}
        initialLessonId={this.state.initialLessonId}
        initialValues={this.state.initialValues}
        loadingInitialValues={this.state.loadingInitialValues}
        ltiKind={this.props.ltiKind}
        modalIsOpen={this.state.modalIsOpen}
        newRecord={this.state.newRecord}
        program={this.props.program}
        stafferId={this.props.userId}
        subscriberConnectedToCanvas={this.props.subscriberConnectedToCanvas}
        updatePath={this.props.updatePath}
      />
    );
  }

  renderActionsDropdown(td, cellData, rowData) {
    ReactDOM.render(
      this.renderDropdown(rowData),
      td
    );
  }

  renderGradebookOption(rowData) {
    if (rowData.assignment_models_hash &&
      Object.values(rowData.assignment_models_hash).flat().some(model => model.classroom_activity)) return;

    if (rowData.classes.length > 0) {
      return (
        <a
          className="dropdown-item"
          href={Routes.teacher_score_path(rowData.classes[0].id, {
            lesson_id: rowData.first_lesson_id,
            program_id: this.props.program.id,
            unit_id: rowData.first_unit_id,
          })}
          role="button"
        >
          <i aria-hidden="true" className="fa fa-bar-chart" />
          {' '}
          {I18n.t('grade')}
        </a>
      );
    }

    return (
      <TCITooltip>
        <TCITooltip.Trigger asChild>
          <span className="dropdown-item link-disabled">
            <i aria-hidden="true" className="fa fa-bar-chart" />
            {' '}
            {I18n.t('grade')}
          </span>
        </TCITooltip.Trigger>
        <TCITooltip.Content>
          Cannot grade assignment assigned to no students.
        </TCITooltip.Content>
      </TCITooltip>
    );
  }

  renderDropdown(rowData) {
    const lmsKind = this.props.studentId ? null : this.lmsKind(rowData);

    return (
      <ActionsDropdown dropdownClassName="dropdown-menu-right">
        <Edit
          openAssignmentModal={this.openAssignmentModal}
          rowData={rowData}
        />

        {this.renderGradebookOption(rowData)}

        {rowData.can_destroy !== false && (
          <Delete
            destroyPath={this.props.destroyPath}
            rowData={rowData}
            updateResources={this.updateResources}
          />
        )}

        {this.props.retroactiveGradeSync &&
          !(rowData.lms_provider && rowData.not_finalized_on_lti) &&
          lmsKind && (
          <>
            <hr />

            <SyncGrades
              assignmentId={rowData.id}
              lmsKind={lmsKind}
            />
          </>
        )}
        {lmsKind && this.props.userType === 'Sysadmin' && (
          <ViewSyncActivity
            assignmentId={rowData.id}
            lmsKind={lmsKind}
          />
        )}
        {lmsKind && rowData.lms_type === 'LTI' && ['Sysadmin', 'SupportManager'].includes(this.props.userType) && (
          <OpenInGradeSync
            assignmentId={rowData.id}
            lmsKind={lmsKind}
          />
        )}
      </ActionsDropdown>
    );
  }

  renderViewedStatus(rowData) {
    let viewedStatus;

    if (this.props.lastViewed) {
      const lastViewed = moment(this.props.lastViewed);

      if (moment(rowData.created_at) > lastViewed) {
        viewedStatus = 'New!';
      }
      else if (moment(rowData.updated_at) > lastViewed) {
        viewedStatus = 'Updated!';
      }
    }
    else {
      viewedStatus = 'New!';
    }

    return viewedStatus && <span className={styles.viewedStatus}>{viewedStatus}</span>;
  }

  renderDatesEnforcedCell(td, cellData, rowData) {
    ReactDOM.render(
      <Fragment>
        {cellData}
        {AssignmentsTable.showLock(rowData.dates_enforced)}
      </Fragment>,
      td
    );
  }

  assignmentInfo = rowData => renderToString(AssignmentDetails({
    rowData: rowData,
    isStudent: this.props.studentId,
    science: this.props.program.science,
    displayUnitContent: this.props.program.display_unit_content
  }));

  assignmentInfoAfterInit(row) {
    if (row) {
      const td = row.child().find('td');
      td?.addClass('no-top-border');
      td?.find(`.${ASSIGNMENT_DETAILS_TOOLTIP_CLASS}`).each((_i, el) => {
        ReactDOM.render(<AssignmentNotStartedInfoIcon />, el);
      });
      td?.find(`.${PAST_DUE_LOCK_TOOLTIP_CLASS}`).each((_i, el) => {
        ReactDOM.render(<PastEnforcedDueDateLockIcon />, el);
      });
    }
  }

  _updateActiveFilters(activeFilters, activeToggle = null) {
    if (activeFilters) {
      this.setState(
        { activeFilters },
        () => this._showFilteredResources()
      );
    }

    if (activeToggle) {
      this.setState(
        () => ({ activeToggle: activeToggle }),
        () => this._showFilteredResources()
      );
    }
  }

  _showFilteredResources() {
    this.setState((prevState) => {
      const filteredResourcesPath = buildFilterPath(this.getPathBase, prevState.activeFilters);

      return ({
        doneLoading: prevState.filterSent || !prevState.doneLoading,
        filterSent: true,
        getPath: filteredResourcesPath,
        loadId: prevState.loadId + 1,
        pagesLoaded: 0,
        resources: []
      });
    });
  }

  _showToggles() {
    return !this.props.inDashboard;
  }

  _addResources(newResources) {
    const filteredResources = this._filterToggledAssignments(newResources);

    const state = {
      doneLoading: newResources.length < this.props.pageSize,
      pagesLoaded: this.state.pagesLoaded + 1,
      resources: filteredResources
    };

    if (this.state.filterSent) {
      state.doneLoading = false;
      state.filterSent = false;
    }

    this.props.setAssignments(filteredResources);

    this.setState(state);
  }

  _filterToggledAssignments(newResources) {
    const filteredResources = [...this.state.resources].concat(...formatAssignments(newResources));

    if (this.props.studentId && this.state.activeToggle !== 'none') {
      return filteredResources.filter(a => a.status === this.state.activeToggle);
    }
    if (this.props.studentId) {
      return filteredResources;
    }
    if (this.state.activeToggle !== 'none' && this.state.activeToggle !== 'all') {
      if (this.state.activeToggle === 'current') {
        return filteredResources.filter(filter => this.props.assignmentsHash.to_do.map(c => c.id).includes(filter.id));
      }

      return filteredResources.filter(filter => this.props.assignmentsHash.past_due.map(c => c.id).includes(filter.id));
    }

    return filteredResources;
  }

  _resetResources() {
    this.setState({
      doneLoading: false,
      pagesLoaded: 0,
      resources: []
    });
  }

  toggleCollapse(e) {
    e.preventDefault();
    Cookie.save('isCollapsed', !this.state.isCollapsed);
    this.setState({ isCollapsed: !this.state.isCollapsed }, () => redirectLinks(this.cardContainer));
  }

  renderFilterTab() {
    return (
      <div
        ref={(node) => { this.cardContainer = node; }}
      >
        {this._renderAdvancedFiltersTitle()}
        {this._renderAdvancedFilters()}
      </div>
    );
  }

  _renderAdvancedFiltersTitle() {
    return (
      <button
        aria-expanded={!this.state.isCollapsed}
        className={styles.titleSection}
        onClick={this.toggleCollapse}
      >
        <div className={styles.title}> {I18n.t('advanced_filters')} </div>
        {this.renderArrow()}
      </button>
    );
  }

  renderArrow() {
    const arrowClass = this.state.isCollapsed ? 'fa-caret-down' : 'fa-caret-up';

    return (
      <div className={styles.arrow}>
        <i aria-hidden="true" className={`fa ${arrowClass}`} />
      </div>
    );
  }

  // Without co-teacher visibility every assignment in the table is the current staffer's, so there is nothing
  // to filter by creator.
  _canFilterByTeacher() {
    return this.props.coTeacherAssignmentVisibility && this.props.teacherAutofillOptions.length > 1;
  }

  _filterProperties() {
    const properties = [
      {
        isDate: true,
        label: 'Due date',
        operators: ['eq', 'not_eq', 'gteq', 'lteq'],
        value: 'due_datetime',
        withArgs: true
      },
      {
        autofillOptions: this.props.lessonAutofillOptions,
        disableSearch: true,
        label: 'Lesson',
        operators: ['eq', 'not_eq'],
        value: 'assignments_models_lesson_id'
      },
    ];

    if (this._canFilterByTeacher()) {
      properties.push({
        autofillOptions: this.props.teacherAutofillOptions,
        label: I18n.t('created_by'),
        operators: ['eq'],
        value: 'staffer_id'
      });
    }

    return properties;
  }

  _renderAdvancedFilters() {
    if (this.state.isCollapsed) return (<div className={styles.borderBottom} />);

    return (
      <div className={styles.borderBottom}>
        <AdvancedFilters
          filterLabel={null}
          initialFilters={this.getInitialFilters()}
          properties={this._filterProperties()}
          resources={this.state.resources}
          showClearButton={false}
          updateActiveFilters={this._updateActiveFilters}
        />
      </div>
    );
  }

  _renderFilterToggles() {
    if (this.props.studentId) {
      return (
        <div aria-label="Assignment filters" className="mb20" role="group">
          <AssignmentFilterButton
            active={this.state.activeToggle === 'to_do'}
            buttonText={`${I18n.t('upcoming')} (${this.props.assignmentCounts.toDo})`}
            handleToggle={this._updateActiveFilters}
            iconStyles={`fa fa-clock-o ${styles.fa} ${styles.clock}`}
            value="to_do"
          />
          <AssignmentFilterButton
            active={this.state.activeToggle === 'past_due'}
            buttonText={`${I18n.t('overdue')} (${this.props.assignmentCounts.pastDue})`}
            handleToggle={this._updateActiveFilters}
            iconStyles={`fa fa-times-circle ${styles.fa} ${styles.timesCircle}`}
            value="past_due"
          />
          <AssignmentFilterButton
            active={this.state.activeToggle === 'completed'}
            buttonText={`${I18n.t('completed')} (${this.props.assignmentCounts.completed})`}
            handleToggle={this._updateActiveFilters}
            iconStyles={`fa fa-check ${styles.fa} ${styles.check}`}
            value="completed"
          />
        </div>
      );
    }
    return (
      <div aria-label="Assignment filters" className="mb20" role="group">
        <AssignmentFilterButton
          active={this.state.activeToggle === 'all'}
          buttonText={`${I18n.t('all')} (${this.props.assignmentCounts.all})`}
          handleToggle={this._updateActiveFilters}
          value="all"
        />
        <AssignmentFilterButton
          active={this.state.activeToggle === 'current'}
          buttonText={`${I18n.t('current')} (${this.props.assignmentCounts.toDo})`}
          handleToggle={this._updateActiveFilters}
          value="current"
        />
        <AssignmentFilterButton
          active={this.state.activeToggle === 'past'}
          buttonText={`${I18n.t('past')} (${this.props.assignmentCounts.pastDue})`}
          handleToggle={this._updateActiveFilters}
          value="past"
        />
      </div>
    );
  }

  _getNoRecordsMessage() {
    if (!this.props.studentId) return 'No data available in table';

    if (this.state.activeToggle === 'to_do') {
      setTimeout(() => {
        const btn = document.getElementById('on-click-target');
        const overdueAssignmentsButton = document.getElementById('assignment-filter-button-past_due');

        if (btn) btn.addEventListener('click', () => overdueAssignmentsButton.click());
      }, 500);

      return I18n.t('no_current_assignments', {
        here: ReactDOMServer.renderToStaticMarkup(
          <button className="btn btn--sm btn--outline-purple mb5" id="on-click-target" type="button">
            {I18n.t('here')}
          </button>
        ),
      });
    }

    if (this.state.activeToggle === 'past_due') return 'You have no assignments past due.';
    if (this.state.activeToggle === 'completed') return 'You have no assignments completed.';

    return 'No data available in table';
  }

  render() {
    return (
      <div className="tw-bg-white">
        <div className={styles.headerItems}>
          {!this.props.inDashboard && (
            <div className={styles['title-with-toggle-buttons']}>
              <h1 className="main_header mt15 mr20">
                {I18n.t('assignments')}
              </h1>
              {['Teacher', 'Coordinator', 'Admin'].includes(this.props.userType) &&
                <SettingsShortcut settingsType="assignments" />
              }
            </div>
          )}
        </div>

        {this._showToggles() && this._renderFilterToggles()}

        {!this.props.studentId && this.renderFilterTab()}

        {this.renderAdd()}

        {this.renderAssignmentModal()}

        <PaginatedTable
          handleResourcesManually
          addResources={this._addResources}
          buttons={[{
            className: 'btn btn--sm btn--outline-purple mr10',
            extend: 'toggleExpandAll'
          }]}
          columnDefs={this.columnDefs()}
          columnMapping={this.props.columns.map(c => (c.name === 'actions' ? null : c.name))}
          columns={this.props.columns}
          defaultExpandRows={this.props.defaultExpandRows}
          defaultOrder={this.defaultOrder()}
          doneLoading={this.state.doneLoading}
          expandCallback={this.assignmentInfo}
          expandCallbackAfterInit={this.assignmentInfoAfterInit}
          getUrl={this.state.getPath}
          hideButtons={this.props.hideButtons}
          info={this.props.info}
          initComplete={this.handleDataTableInit}
          noRecordsMessage={this._getNoRecordsMessage()}
          pageLength={this.props.pageLength}
          pagesLoaded={this.state.pagesLoaded}
          paging={this.props.paging}
          resetResources={this._resetResources}
          resources={this.state.resources}
          showPagingOptions={this.props.showPagingOptions}
        />
      </div>
    );
  }
}
